import { useEffect, useState } from "react";
import Papa from "papaparse";
import { api, formatApiError, notifyMutation } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, PencilSimple, Stack, DownloadSimple } from "@phosphor-icons/react";
import { DeleteStockGuard, EditStockConfirm, fetchStockSummary } from "@/components/StockGuard";
import SeriesImportDialog from "@/components/SeriesImportDialog";

export default function SeriesPage() {
  const { isStaff, isAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const [mfrs, setMfrs] = useState([]);
  const [filterMfr, setFilterMfr] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ manufacturer_id: "", name: "", description: "" });
  const [editStock, setEditStock] = useState(null);
  const [confirmEditOpen, setConfirmEditOpen] = useState(false);

  const load = async () => {
    const [{ data: s }, { data: m }] = await Promise.all([api.get("/series"), api.get("/manufacturers")]);
    setItems(s); setMfrs(m);
  };
  useEffect(() => { load(); }, []);

  const filtered = filterMfr === "all" ? items : items.filter((s) => s.manufacturer_id === filterMfr);
  const mfrMap = Object.fromEntries(mfrs.map((m) => [m.id, m]));

  const openCreate = () => { setEditing(null); setEditStock(null); setForm({ manufacturer_id: mfrs[0]?.id || "", name: "", description: "" }); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setEditStock(null); setForm({ manufacturer_id: s.manufacturer_id, name: s.name, description: s.description || "" }); setOpen(true); };

  const doSave = async () => {
    setConfirmEditOpen(false);
    try {
      let res;
      if (editing) res = await api.put(`/series/${editing.id}`, form);
      else res = await api.post("/series", form);
      notifyMutation(res, editing ? "Updated" : "Created");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const handleSave = async () => {
    if (!editing) { doSave(); return; }
    // Always re-validate fresh at save time (never trust cached/stale stock state).
    const stock = await fetchStockSummary("series", editing.id);
    if (stock.error) { toast.error("Couldn't verify stock — please try again."); return; }
    setEditStock(stock);
    if (stock.has_stock) { setConfirmEditOpen(true); return; }
    doSave();
  };

  const onDelete = async (s) => {
    try { const res = await api.delete(`/series/${s.id}`); notifyMutation(res, "Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const exportCsv = () => {
    if (filtered.length === 0) { toast.error("No series to export"); return; }
    const rows = filtered.map((s) => ({
      manufacturer_name: mfrMap[s.manufacturer_id]?.name || "",
      series_name: s.name || "",
      description: s.description || "",
    }));
    const csv = Papa.unparse({ fields: ["manufacturer_name", "series_name", "description"], data: rows });
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `vwa-series-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} series to CSV`);
  };

  return (
    <div className="p-8 max-w-[1200px]">
      <header className="flex items-end justify-between mb-8">
        <div>
          <div className="text-xs font-mono tracking-[0.2em] uppercase text-slate-500 mb-2">Catalogue</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-slate-900">Material Series</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={exportCsv} className="gap-2 rounded-md" data-testid="export-series-csv-btn">
            <DownloadSimple size={16} /> Export CSV
          </Button>
          {isAdmin && <SeriesImportDialog onSuccess={load} />}
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openCreate} disabled={mfrs.length === 0} className="bg-blue-600 hover:bg-blue-700 gap-2 rounded-md" data-testid="add-series-btn">
                <Plus size={16} /> Add Series
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{editing ? "Edit" : "New"} Series</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2">
                <div className="space-y-1.5">
                  <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Manufacturer</Label>
                  <Select value={form.manufacturer_id} onValueChange={(v) => setForm({ ...form, manufacturer_id: v })}>
                    <SelectTrigger data-testid="series-mfr-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {mfrs.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Name</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="2080, CG, HM…" data-testid="series-name-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Description</Label>
                  <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)} className="rounded-md">Cancel</Button>
                <Button onClick={handleSave} disabled={!form.name || !form.manufacturer_id} className="bg-blue-600 hover:bg-blue-700 rounded-md" data-testid="series-save-btn">Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      <div className="mb-4 flex flex-wrap gap-2">
        <button onClick={() => setFilterMfr("all")} className={`px-3 py-1.5 text-xs font-medium border rounded-md ${filterMfr === "all" ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200"}`}>All</button>
        {mfrs.map((m) => (
          <button key={m.id} onClick={() => setFilterMfr(m.id)}
            className={`px-3 py-1.5 text-xs font-medium border rounded-md ${filterMfr === m.id ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200"}`}>{m.name}</button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="border border-dashed border-slate-300 bg-white p-16 text-center">
          <Stack size={32} className="mx-auto text-slate-300 mb-3" />
          <div className="text-sm text-slate-500">No series yet.</div>
        </div>
      ) : (
        <div className="border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500">
                <th className="px-6 py-3">Manufacturer</th>
                <th className="px-6 py-3">Series</th>
                <th className="px-6 py-3">Description</th>
                <th className="px-6 py-3 w-32"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => (
                <tr key={s.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                  <td className="px-6 py-3">{mfrMap[s.manufacturer_id]?.name}</td>
                  <td className="px-6 py-3 font-medium">{s.name}</td>
                  <td className="px-6 py-3 text-slate-600">{s.description || "—"}</td>
                  <td className="px-6 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(s)} data-testid={`edit-series-${s.id}`}><PencilSimple size={14} /></Button>
                      <DeleteStockGuard entityType="series" id={s.id} name={s.name} isStaff={isStaff} onConfirm={() => onDelete(s)} testId={`delete-series-${s.id}`} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <EditStockConfirm open={confirmEditOpen} onOpenChange={setConfirmEditOpen} entityType="series" name={editing?.name} summary={editStock?.summary} onConfirm={doSave} />
    </div>
  );
}
